import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { StoreProvider } from "@/context/StoreContext";
import SiteLayout from "@/components/layout/SiteLayout";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import PaymentPage from "@/pages/PaymentPage";
import Support from "@/pages/Support";
import AuthCallback from "@/pages/AuthCallback";
import { CategoriesPage, FavoritesPage, PromotionsPage } from "@/pages/CatalogPages";
import { ServicesPage, UnlockPage } from "@/pages/ServicePages";
import { ForgotPasswordPage, LoginPage, RegisterPage } from "@/pages/AuthPages";
import AccountLayout, { RequireAuth } from "@/pages/account/AccountLayout";
import AccountOrderDetail from "@/pages/account/AccountOrderDetail";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import {
  AccountDashboard,
  AccountDownloads,
  AccountInvoices,
  AccountLicenses,
  AccountOrders,
  AccountProfile,
  AccountSecurity,
  AccountSubscriptions,
  AccountTickets,
} from "@/pages/account/AccountPages";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPaymentMethods from "@/pages/admin/AdminPaymentMethods";
import AdminPaymentsVerify from "@/pages/admin/AdminPaymentsVerify";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminSlides from "@/pages/admin/AdminSlides";
import AdminLicenses from "@/pages/admin/AdminLicenses";
import AdminPrivateNotes from "@/pages/admin/AdminPrivateNotes";
import AdminContent from "@/pages/admin/AdminContent";
import AdminDeliveries from "@/pages/admin/AdminDeliveries";
import AdminDeliveryDetail from "@/pages/admin/AdminDeliveryDetail";
import AccountDelivery from "@/pages/account/AccountDelivery";
import AccountDeliveryLaunch from "@/pages/account/AccountDeliveryLaunch";
import Formations from "@/pages/Formations";
import FormationDetail from "@/pages/FormationDetail";
import FormationLearn from "@/pages/FormationLearn";
import AccountFormations from "@/pages/account/AccountFormations";
import AdminFormations from "@/pages/admin/AdminFormations";
import AdminFormationStudents from "@/pages/admin/AdminFormationStudents";
import Offres from "@/pages/Offres";
import AdminBundles from "@/pages/admin/AdminBundles";
import DevenirRevendeur from "@/pages/DevenirRevendeur";
import Boutiques from "@/pages/Boutiques";
import VendorShopPage from "@/pages/VendorShopPage";
import VendorLayout from "@/pages/vendor/VendorLayout";
import VendorDashboard from "@/pages/vendor/VendorDashboard";
import VendorProducts from "@/pages/vendor/VendorProducts";
import VendorProductForm from "@/pages/vendor/VendorProductForm";
import VendorFormations from "@/pages/vendor/VendorFormations";
import VendorOrders from "@/pages/vendor/VendorOrders";
import VendorOrderDetail from "@/pages/vendor/VendorOrderDetail";
import VendorClients from "@/pages/vendor/VendorClients";
import VendorSales from "@/pages/vendor/VendorSales";
import VendorStats from "@/pages/vendor/VendorStats";
import VendorSubscription from "@/pages/vendor/VendorSubscription";
import VendorPayments from "@/pages/vendor/VendorPayments";
import VendorShop from "@/pages/vendor/VendorShop";
import VendorProfile from "@/pages/vendor/VendorProfile";
import VendorSupport from "@/pages/vendor/VendorSupport";
import AdminVendors from "@/pages/admin/AdminVendors";
import AdminVendorRequests from "@/pages/admin/AdminVendorRequests";
import AdminVendorSales from "@/pages/admin/AdminVendorSales";
import AdminVendorPlans from "@/pages/admin/AdminVendorPlans";
import AdminVendorActivities from "@/pages/admin/AdminVendorActivities";
import AdminAiAnalytics from "@/pages/admin/AdminAiAnalytics";
import AdminMenus from "@/pages/admin/AdminMenus";
import VendorLicenses from "@/pages/vendor/VendorLicenses";
import VendorDeliveries from "@/pages/vendor/VendorDeliveries";
import VendorPaymentsVerify from "@/pages/vendor/VendorPaymentsVerify";
import VendorPromos from "@/pages/vendor/VendorPromos";
import VendorReviews from "@/pages/vendor/VendorReviews";
import VendorStudents from "@/pages/vendor/VendorStudents";
import VendorNotes from "@/pages/vendor/VendorNotes";
import VendorAi from "@/pages/vendor/VendorAi";
import VendorBundles from "@/pages/vendor/VendorBundles";
import VendorPublications from "@/pages/vendor/VendorPublications";
import AccountActivity from "@/pages/account/AccountActivity";
import CertificatePage from "@/pages/CertificatePage";
import VisualSearch from "@/pages/VisualSearch";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import AdminSiteStatus from "@/pages/admin/AdminSiteStatus";
import AdminWhatsAppLogs from "@/pages/admin/AdminWhatsAppLogs";
import AdminWhatsAppMessage from "@/pages/admin/AdminWhatsAppMessage";
import AdminPwaSettings from "@/pages/admin/AdminPwaSettings";
import AdminPromoPopup from "@/pages/admin/AdminPromoPopup";
import AdminPublications from "@/pages/admin/AdminPublications";
import MaintenanceGate from "@/components/MaintenanceGate";
import ResellerGate from "@/components/ResellerGate";
import { SiteStatusProvider } from "@/context/SiteStatusContext";
import {
  AdminCategories,
  AdminCustomers,
  AdminLogs,
  AdminOrders,
  AdminPromos,
  AdminReviews,
  AdminTickets,
} from "@/pages/admin/AdminTables";

import AdminAlarmSoundSettings from "@/pages/admin/AdminAlarmSoundSettings";
function NotFound() {
  return (
    <div className="container-it py-24 text-center">
      <p className="font-display text-6xl font-black text-brand">404</p>
      <h1 className="mt-4 font-display text-2xl font-extrabold text-ink">Page introuvable</h1>
      <p className="mt-2 text-sm text-slate-500">La page que vous cherchez n'existe pas ou a été déplacée.</p>
      <a href="/" className="mt-6 inline-block rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">
        Retour à l'accueil
      </a>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <MaintenanceGate>
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/boutique" element={<Shop />} />
        <Route path="/boutique/:slug" element={<ProductDetail />} />
        <Route path="/formations" element={<Formations />} />
        <Route path="/formations/:slug" element={<FormationDetail />} />
        <Route path="/offres" element={<Offres />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/promotions" element={<PromotionsPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/deblocage" element={<UnlockPage />} />
        <Route path="/support" element={<Support />} />
        <Route path="/devenir-revendeur" element={<ResellerGate><DevenirRevendeur /></ResellerGate>} />
        <Route path="/boutiques" element={<ResellerGate><Boutiques /></ResellerGate>} />
        <Route path="/boutique-vendeur/:slug" element={<ResellerGate><VendorShopPage /></ResellerGate>} />
        <Route path="/certificat/:number" element={<CertificatePage />} />
        <Route path="/recherche-visuelle" element={<VisualSearch />} />
        <Route path="/panier" element={<Cart />} />
        <Route path="/commander" element={<Checkout />} />
        <Route path="/commande/:orderNumber" element={<OrderConfirmation />} />
        <Route path="/paiement/:orderNumber" element={<PaymentPage />} />
        <Route path="/favoris" element={<FavoritesPage />} />
        <Route path="/connexion" element={<LoginPage />} />
        <Route path="/inscription" element={<RegisterPage />} />
        <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />

        <Route
          path="/compte"
          element={
            <RequireAuth>
              <AccountLayout />
            </RequireAuth>
          }
        >
          <Route index element={<AccountDashboard />} />
          <Route path="commandes" element={<AccountOrders />} />
          <Route path="commandes/:id" element={<AccountOrderDetail />} />
          <Route path="licences" element={<AccountLicenses />} />
          <Route path="formations" element={<AccountFormations />} />
          <Route path="telechargements" element={<AccountDownloads />} />
          <Route path="factures" element={<AccountInvoices />} />
          <Route path="favoris" element={<FavoritesPage />} />
          <Route path="abonnements" element={<AccountSubscriptions />} />
          <Route path="tickets" element={<AccountTickets />} />
          <Route path="activite" element={<AccountActivity />} />
          <Route path="profil" element={<AccountProfile />} />
          <Route path="securite" element={<AccountSecurity />} />
        </Route>

        <Route
          path="/compte/commandes/:id/telechargement"
          element={
            <RequireAuth>
              <AccountDelivery />
            </RequireAuth>
          }
        />
        <Route
          path="/compte/commandes/:id/telechargement/:itemId"
          element={
            <RequireAuth>
              <AccountDeliveryLaunch />
            </RequireAuth>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Route>

      <Route
        path="/admin"
        element={
          <RequireAuth adminOnly>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="produits" element={<AdminProducts />} />
        <Route path="formations" element={<AdminFormations />} />
        <Route path="formations/etudiants" element={<AdminFormationStudents />} />
        <Route path="offres" element={<AdminBundles />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="licences" element={<AdminLicenses />} />
        <Route path="commandes" element={<AdminOrders />} />
        <Route path="commandes/:id" element={<AdminOrderDetail />} />
        <Route path="livraisons" element={<AdminDeliveries />} />
        <Route path="livraisons/:id" element={<AdminDeliveryDetail />} />
        <Route path="moyens-paiement" element={<AdminPaymentMethods />} />
        <Route path="paiements-verifier" element={<AdminPaymentsVerify />} />
        <Route path="clients" element={<AdminCustomers />} />
        <Route path="bannieres" element={<AdminSlides />} />
        <Route path="promotions" element={<AdminPromos />} />
        <Route path="avis" element={<AdminReviews />} />
        <Route path="tickets" element={<AdminTickets />} />
        <Route path="journal" element={<AdminLogs />} />
        <Route path="contenu" element={<AdminContent />} />
        <Route path="revendeurs" element={<AdminVendors />} />
        <Route path="revendeurs/ventes" element={<AdminVendorSales />} />
        <Route path="revendeurs/demandes" element={<AdminVendorRequests />} />
        <Route path="revendeurs/abonnements" element={<AdminVendorPlans />} />
        <Route path="revendeurs/activites" element={<AdminVendorActivities />} />
        <Route path="ia-analytics" element={<AdminAiAnalytics />} />
        <Route path="menus" element={<AdminMenus />} />
        <Route path="maintenance" element={<AdminSiteStatus />} />
        <Route path="whatsapp" element={<AdminWhatsAppLogs />} />
        <Route path="message-whatsapp" element={<AdminWhatsAppMessage />} />
        <Route path="pwa" element={<AdminPwaSettings />} />
        <Route path="sonneries" element={<AdminAlarmSoundSettings />} />
        <Route path="promo-popup" element={<AdminPromoPopup />} />
        <Route path="publications" element={<AdminPublications />} />
        <Route path="notes-privees" element={<AdminPrivateNotes />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>

      <Route path="/vendeur" element={<ResellerGate><VendorLayout /></ResellerGate>}>
        <Route index element={<Navigate to="/vendeur/dashboard" replace />} />
        <Route path="dashboard" element={<VendorDashboard />} />
        <Route path="produits" element={<VendorProducts />} />
        <Route path="produits/nouveau" element={<VendorProductForm />} />
        <Route path="produits/:id" element={<VendorProductForm />} />
        <Route path="licences" element={<VendorLicenses />} />
        <Route path="formations" element={<VendorFormations />} />
        <Route path="etudiants" element={<VendorStudents />} />
        <Route path="livraisons" element={<VendorDeliveries />} />
        <Route path="paiements-verifier" element={<VendorPaymentsVerify />} />
        <Route path="promos" element={<VendorPromos />} />
        <Route path="offres" element={<VendorBundles />} />
        <Route path="publications" element={<VendorPublications />} />
        <Route path="avis" element={<VendorReviews />} />
        <Route path="notes" element={<VendorNotes />} />
        <Route path="commandes" element={<VendorOrders />} />
        <Route path="commandes/:id" element={<VendorOrderDetail />} />
        <Route path="clients" element={<VendorClients />} />
        <Route path="ventes" element={<VendorSales />} />
        <Route path="statistiques" element={<VendorStats />} />
        <Route path="abonnement" element={<VendorSubscription />} />
        <Route path="paiements" element={<VendorPayments />} />
        <Route path="boutique" element={<VendorShop />} />
        <Route path="profil" element={<VendorProfile />} />
        <Route path="support" element={<VendorSupport />} />
        <Route path="ia" element={<VendorAi />} />
        <Route path="*" element={<Navigate to="/vendeur/dashboard" replace />} />
      </Route>

      <Route
        path="/mes-formations/:formationId"
        element={
          <RequireAuth>
            <FormationLearn />
          </RequireAuth>
        }
      />
      <Route
        path="/mes-formations/:formationId/:lessonId"
        element={
          <RequireAuth>
            <FormationLearn />
          </RequireAuth>
        }
      />
    </Routes>
    </MaintenanceGate>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteStatusProvider>
        <StoreProvider>
          <AppRoutes />
          <PwaInstallPrompt />
          <Toaster position="top-center" richColors closeButton />
        </StoreProvider>
        </SiteStatusProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
